
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ProductCard } from './ProductCard';
import { supabase } from '../lib/supabaseClient';

async function fetchRelatedProducts(asin, features = [], category = '', brand = '') {
  try {
    let query = supabase
      .from('products')
      .select('*')
      .neq('asin', asin) // Exclude the current product
      .limit(8);

    // First try to find products with similar features
    if (features && features.length > 0) {
      const keywords = features.join(' ').toLowerCase().split(' ')
        .filter(word => word.length > 3) // Filter out short words
        .slice(0, 5); // Limit to 5 keywords
      
      if (keywords.length > 0) {
        // Use textSearch instead of complex pattern matching
        const searchTerm = keywords.join(' & ');
        query = query.textSearch('title', searchTerm);
      }
    }
    
    const { data, error } = await query;

    if (error) {
      console.error('Related products query error:', error);
      // Fallback to category-based search
      if (category) {
        const { data: categoryData, error: categoryError } = await supabase
          .from('products')
          .select('*')
          .neq('asin', asin)
          .eq('category_slug', category)
          .limit(8);
        
        if (!categoryError) {
          return categoryData || [];
        }
      }
      throw error;
    }

    // If we have less than 4 products and brand is available, try brand-based search
    if (data && data.length < 4 && brand) {
      const { data: brandProducts, error: brandError } = await supabase
        .from('products')
        .select('*')
        .neq('asin', asin)
        .ilike('brand', `%${brand}%`)
        .limit(8 - data.length);
      
      if (!brandError && brandProducts) {
        return [...data, ...brandProducts];
      }
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching related products:', error);
    
    // Final fallback - get random products from same category
    try {
      if (category) {
        const { data: fallbackData } = await supabase
          .from('products')
          .select('*')
          .neq('asin', asin)
          .eq('category_slug', category)
          .limit(8);
        
        return fallbackData || [];
      }
    } catch (fallbackError) {
      console.error('Fallback query also failed:', fallbackError);
    }
    
    return [];
  }
}

export function RelatedProducts({ asin, features, category, brand, title }) {
  const { data: relatedProducts, isLoading } = useQuery({
    queryKey: ['relatedProducts', asin, category, brand],
    queryFn: () => fetchRelatedProducts(asin, features, category, brand),
    enabled: !!asin
  });

  if (isLoading) {
    return (
      <div className="mt-12">
        <h2 className="text-2xl font-bold mb-6">Related Products</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="bg-gray-200 aspect-square rounded-lg mb-2"></div>
              <div className="h-4 bg-gray-200 rounded mb-1"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!relatedProducts || relatedProducts.length === 0) {
    return null;
  }

  return (
    <div className="mt-12">
      <h2 className="text-2xl font-bold mb-6">Related Products</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {relatedProducts.slice(0, 8).map((product) => (
          <ProductCard key={product.asin} product={product} view="grid" />
        ))}
      </div>
    </div>
  );
}
